import { Typography } from '@mui/material';
import { createInvoicePageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { VyIcon } from '@vy/ui-design-system';
import type { CreditPurchaseConfig, Currency, FixedProductInput } from '@vy/types';
import { isEmptyArray, isNotEmptyArray, withDefault } from '@vy/utils';
import React, { Suspense } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { AddIcon } from '../../../../../../components/icons/AddIcon';
import { Spinner } from '../../../../../../components/design/Spinner';
import { useDialog } from '../../../../../../hooks/use-dialog';
import {
  AddCreditDrawer,
  buildCreditPurchaseConfig,
} from '../../../../../plans/CreatePlanPage/create-plan-page-components/AddCreditDrawer';
import type { AddCreditForm } from '../../../../../plans/CreatePlanPage/create-plan-page-components/AddCreditDrawer';
import { CreditsSection } from '../../../../../plans/CreatePlanPage/create-plan-page-components/CreditsEditTable/CreditsSection';
import { fromCredit } from '../../../../../plans/CreatePlanPage/create-plan-page-components/CreditsEditTable/credit-grant-view';
import type { CreditGrantView } from '../../../../../plans/CreatePlanPage/create-plan-page-components/CreditsEditTable/credit-grant-view';
import type { NewPlanForm } from '../../../../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../../../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';
import { AddItemMenuButton } from '../../../../../plans/CreatePlanPage/create-plan-page-components/CreatePlanPageTabs/create-plan-page-tabs-components/AddItemMenuButton';
import { EmptyTab } from '../../../../../plans/CreatePlanPage/create-plan-page-components/CreatePlanPageTabs/create-plan-page-tabs-components/EmptyTab';
import { FixedChargesEditTable } from '../../../../../plans/CreatePlanPage/create-plan-page-components/FixedChargesEditTable/FixedChargesEditTable';
import { CreateInvoicePageFixedChargesMenuItems } from './CreateInvoicePageFixedChargesMenuItems';

type CreateInvoicePageFixedChargesTabContentProps = {
  fixedProducts: FixedProductInput[];
  currency: Currency;
};

export const CreateInvoicePageFixedChargesTabContent: React.FC<
  CreateInvoicePageFixedChargesTabContentProps
> = ({ fixedProducts }) => {
  const { control, getValues, setValue } = useFormContext<PlanInputForm, NewPlanForm>();

  const {
    isDialogOpen: isAddCreditDrawerOpen,
    handleOpen: handleOpenAddCredit,
    handleClose: handleCloseAddCredit,
  } = useDialog();

  const credits = (useWatch({ control, name: 'credits' }) ?? []) as CreditPurchaseConfig[];
  const creditRows: CreditGrantView[] = credits.map((c, i) => fromCredit(c, i));
  const hasAnyCredits = creditRows.length > 0;

  const handleAddCredit = (data: AddCreditForm) => {
    const current = withDefault(getValues('credits'), []);
    setValue('credits', [...current, buildCreditPurchaseConfig(data)], { shouldDirty: true });
  };

  if (isEmptyArray(fixedProducts) && !hasAnyCredits) {
    return (
      <div className="flex w-full flex-col gap-y-4">
        <EmptyTab
          title="Create and Add Fixed Fees"
          description="Set up one-time fees, subscription charges or price per unit to tailor your pricing model."
        >
          <AddItemMenuButton
            cta={
              <span className="flex items-center gap-x-2">
                <AddIcon fill="white" />
                <span>Add Fees</span>
              </span>
            }
            {...getSelectorAttribute(createInvoicePageSelectors.fixedChargesTab.addFeesButton)}
          >
            <CreateInvoicePageFixedChargesMenuItems onAddCreditClick={handleOpenAddCredit} />
          </AddItemMenuButton>
        </EmptyTab>

        {isAddCreditDrawerOpen && (
          <AddCreditDrawer
            open={isAddCreditDrawerOpen}
            onClose={handleCloseAddCredit}
            onAddCredit={handleAddCredit}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-y-4">
      <div className="flex items-center justify-end">
        <AddItemMenuButton
          color="secondary"
          cta={
            <span className="flex items-center gap-x-2">
              <VyIcon
                icon="PlusMedium"
                style={{
                  transform: 'scale(0.8)',
                }}
              />
              <span>Add Item</span>
            </span>
          }
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <CreateInvoicePageFixedChargesMenuItems onAddCreditClick={handleOpenAddCredit} />
        </AddItemMenuButton>
      </div>

      {isNotEmptyArray(fixedProducts) && (
        <>
          <Typography className="text-base font-medium">Service Fees</Typography>

          <Suspense fallback={<Spinner size={75} />}>
            <FixedChargesEditTable fixedProducts={fixedProducts} isInvoice />
          </Suspense>
        </>
      )}

      {hasAnyCredits && <CreditsSection rows={creditRows} />}

      {isAddCreditDrawerOpen && (
        <AddCreditDrawer
          open={isAddCreditDrawerOpen}
          onClose={handleCloseAddCredit}
          onAddCredit={handleAddCredit}
        />
      )}
    </div>
  );
};
